/**
 * Отрисовка экрана по текущему состоянию.
 *
 * Выделено из App, чтобы оркестрация переходов и разметка не жили в одной
 * функции (docs/TECHNICAL_SPECIFICATION.md NFR-Q-02, NFR-Q-03).
 */

import type { LanguageCode } from '@type2x/shared-contracts';
import type { StartPath } from '@type2x/feature-personal-workspace';
import type { Translate } from '@type2x/i18n';
import type { TypingSession } from '@type2x/typing-engine';
import { hintLevelFor } from '@type2x/learning-engine';
import type { OnboardingChoice } from '@type2x/feature-onboarding';
import type { SymbolMode } from '@type2x/feature-symbols';
import type { PackMode } from '@type2x/feature-packs';
import type { TempoMode } from '@type2x/feature-tempo';
import type { RegistrationOutcome, SignedIn } from '@type2x/feature-accounts';
import type { IntegritySignals } from '@type2x/adapter-web';

import type { Runtime } from '../runtime.js';
import type { AppScreen, LockedTarget } from '../state/app-state.js';
import type { ScriptReadiness } from '../state/script-readiness.js';
import type { VerificationState } from '../state/verification.js';
import { OnboardingScreen } from './OnboardingScreen.js';
import { LanguageSetupScreen } from './LanguageSetupScreen.js';
import { LeaderboardRoute } from './LeaderboardRoute.js';
import { HomeScreen } from './HomeScreen.js';
import { LockedModeScreen } from './LockedModeScreen.js';
import { TypingScreen } from './TypingScreen.js';
import { ControlTestScreen } from './ControlTestScreen.js';
import { ResultScreen } from './ResultScreen.js';
import { DiagnosticResultScreen } from './DiagnosticResultScreen.js';
import { AccountScreen } from './AccountScreen.js';
import { PasswordResetScreen } from './PasswordResetScreen.js';
import { ControlResultScreen } from './ControlResultScreen.js';
import { MistakesScreen } from './MistakesScreen.js';
import { SentencesScreen } from './SentencesScreen.js';
import { FundamentalsScreen } from './FundamentalsScreen.js';
import { SymbolsScreen } from './SymbolsScreen.js';
import { PacksScreen } from './PacksScreen.js';
import { LayoutSwitchScreen } from './LayoutSwitchScreen.js';
import { ReportScreen } from './ReportScreen.js';
import { AchievementsScreen } from './AchievementsScreen.js';
import { TempoScreen } from './TempoScreen.js';
import { CustomTextScreen } from './CustomTextScreen.js';

export interface ScreenRouterProps {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly screen: AppScreen;
  readonly onOnboardingDone: (choice: OnboardingChoice) => void;
  readonly onDiagnosticsDone: (words: readonly string[], session: TypingSession) => void;
  readonly onContinueAfterDiagnostics: () => void;
  readonly onRetakeDiagnostics: () => void;
  readonly onPracticeDone: (session: TypingSession) => void;
  readonly onAgain: () => void;
  readonly onSaveProgress: () => void;
  readonly onRegister: (email: string, password: string) => Promise<RegistrationOutcome>;
  readonly onLogin: (email: string, password: string) => Promise<SignedIn>;
  readonly onReadSession: () => Promise<SignedIn | null>;
  readonly onResendVerification: (email: string) => Promise<void>;
  readonly onRequestPasswordReset: (email: string) => Promise<void>;
  readonly onConfirmPasswordReset: (token: string, password: string) => Promise<void>;
  readonly onExportProgress: () => Promise<void>;
  readonly onDeleteAccount: (password: string) => Promise<void>;
  /** Выход из аккаунта; `pending` — очередь занятий не разобрана. */
  readonly onLogout: (force?: boolean) => Promise<'done' | 'pending'>;
  readonly onConsentChange: (optIn: boolean) => void;
  readonly consent: boolean;
  readonly verification: VerificationState;
  readonly onContinueAfterVerification: () => void;
  readonly onControlTestDone: (session: TypingSession, signals: IntegritySignals) => void;
  readonly onStartControlTest: () => void;
  readonly onBackToPractice: () => void;
  readonly onStartMistakes: () => void;
  readonly onMistakeStageDone: (run: TypingSession) => void;
  readonly onStartSentences: () => void;
  readonly onSentenceDone: (run: TypingSession) => void;
  readonly onStartFundamentals: () => void;
  readonly onFundamentalsDone: (run: TypingSession) => void;
  readonly onStartSymbols: (mode: SymbolMode) => void;
  readonly onSymbolsDone: (run: TypingSession) => void;
  readonly onStartPacks: (mode: PackMode) => void;
  readonly onPacksDone: (run: TypingSession) => void;
  readonly onOpenReport: () => void;
  readonly onOpenAchievements: () => void;
  readonly onOpenLeaderboard: () => void;
  /** Показывать ли достижения, задания, серии и XP (ТЗ FR-GM-08). */
  readonly gameElements: boolean;
  readonly onStartLayoutSwitch: () => void;
  readonly onLayoutSwitchDone: (run: TypingSession) => void;
  readonly onStartTempo: (mode: TempoMode) => void;
  readonly onTempoRunDone: (run: TypingSession) => void;
  readonly onStartCustomText: () => void;
  readonly onCustomTextSubmit: (text: string) => void;
  readonly onCustomTextDone: (run: TypingSession) => void;
  /** Режимы символов, которым ещё есть чему учить. */
  readonly symbolModes: readonly SymbolMode[];
  /** Режимы пакетов, которым ещё есть чему учить. */
  readonly packModes: readonly PackMode[];
  /** Остались ли непройденные группы клавиш в режиме «Основы». */
  readonly fundamentalsAvailable: boolean;
  /** Есть ли слабые места, ради которых стоит открывать режим. */
  readonly mistakesAvailable: boolean;
  /** Наступил ли срок отложенной проверки сохранения навыка. */
  readonly retentionDue: boolean;
  /** Ведёт ли основная колея в «Основы»: экран результата называет, куда ведёт. */
  readonly resumesToFundamentals: boolean;
  /** Освоена ли латиница: от неё зависят режимы кода и адресов. */
  readonly latin: ScriptReadiness;
  /** Освоены ли обе раскладки режима переключения. */
  readonly layoutSwitch: ScriptReadiness;
  /** Освоение собственной раскладки: измеримый контекст карточки продолжения. */
  readonly keys: ScriptReadiness;
  /** Первый вход в новый язык тренировки: выбор сделан или отменён. */
  readonly onLanguageSetupDone: (choice: OnboardingChoice) => void;
  readonly onLanguageSetupCancel: () => void;
  /** Смена языка тренировки: ею заканчивается объяснение закрытых режимов. */
  readonly onTrainingLanguageChange: (language: LanguageCode) => void;
  /** Язык тренировки: рейтинг считается отдельно для каждого (ТЗ FR-LB-01). */
  readonly trainingLanguage: LanguageCode | null;
  /** С чего идёт обучение языку и как это поменять. */
  readonly startPath: StartPath;
  readonly onStartPathChange: (path: StartPath) => void;
  /** Открыть закрытый режим всё равно и вернуться назад. */
  readonly onProceedLocked: (target: LockedTarget) => void;
  readonly onBack: () => void;
}

export function ScreenRouter(props: ScreenRouterProps): React.JSX.Element {
  const { runtime, t, screen } = props;

  // Режимы, открытые с результата, разбираются отдельно: в общем
  // переключателе они отличались бы только именем компонента.
  if (isModeScreen(screen)) {
    return <ModeCase runtime={runtime} t={t} screen={screen} props={props} />;
  }

  // Первый вход — в приложение или в новый язык — тоже разбирается отдельно:
  // это один и тот же экран вопросов, отличается только их набор.
  if (isEntryScreen(screen)) {
    return <EntryCase t={t} screen={screen} props={props} />;
  }

  return <MainCase runtime={runtime} t={t} screen={screen} props={props} />;
}

/**
 * Остальные экраны: обучение, отчёт, аккаунт.
 *
 * Вынесены из корневого переключателя не ради красоты: он разросся до предела
 * сложности, за которым его перестают читать целиком (ТЗ NFR-Q-02).
 */
function MainCase({
  runtime,
  t,
  screen,
  props,
}: {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly screen: Exclude<AppScreen, ModeScreen | EntryScreen>;
  readonly props: ScreenRouterProps;
}): React.JSX.Element {
  switch (screen.name) {
    case 'loading':
      return <p>{t('app.title')}</p>;

    case 'home':
      return <HomeCase props={props} />;

    case 'locked-mode':
      return <LockedCase target={screen.target} props={props} />;

    case 'diagnostics':
      return (
        <TypingScreen
          // Ключ по идентификатору сессии: новая сессия набора — новый
          // компонент, иначе состояние предыдущей сессии перетекло бы в неё.
          key={screen.sessionId}
          runtime={runtime}
          t={t}
          title={t('diagnostics.title')}
          hint={t('diagnostics.hint')}
          sessionId={screen.sessionId}
          layoutId={screen.task.layoutId}
          targetText={screen.task.text}
          hintLevel="delayed-hint"
          onFinished={(session) => props.onDiagnosticsDone(screen.task.words, session)}
        />
      );

    case 'practice':
      return (
        <PracticeScreen runtime={runtime} t={t} screen={screen} onFinished={props.onPracticeDone} />
      );

    case 'diagnostic-result':
    case 'result':
      return <ResultsCase t={t} screen={screen} props={props} />;

    case 'report':
      return <ReportScreen t={t} report={screen.report} onBack={props.onBackToPractice} />;

    case 'leaderboard':
      return (
        <LeaderboardRoute
          runtime={runtime}
          t={t}
          language={props.trainingLanguage ?? 'ru'}
          onSaveProgress={props.onSaveProgress}
          onBack={props.onBackToPractice}
        />
      );

    case 'achievements':
      return <AchievementsScreen t={t} view={screen.view} onBack={props.onBackToPractice} />;

    case 'control-test':
      return (
        <ControlTestScreen
          runtime={runtime}
          t={t}
          sessionId={screen.sessionId}
          layoutId={screen.task.layoutId}
          targetText={screen.task.text}
          retention={screen.task.isRetentionCheck}
          onFinished={props.onControlTestDone}
        />
      );

    case 'control-result':
      return (
        <ControlResultScreen
          t={t}
          outcome={screen.outcome}
          metrics={screen.metrics}
          onBack={props.onBackToPractice}
          onSaveProgress={props.onSaveProgress}
        />
      );

    case 'password-reset':
      return (
        <PasswordResetScreen
          t={t}
          token={screen.token}
          onSubmit={props.onConfirmPasswordReset}
          onSignIn={props.onSaveProgress}
        />
      );

    case 'account':
      return (
        <AccountScreen
          t={t}
          onSubmit={props.onRegister}
          onLogin={props.onLogin}
          onResend={props.onResendVerification}
          onForgotPassword={props.onRequestPasswordReset}
          onExport={props.onExportProgress}
          onDelete={props.onDeleteAccount}
          onLogout={props.onLogout}
          onConsentChange={props.onConsentChange}
          consent={props.consent}
          readSession={props.onReadSession}
          verification={props.verification}
          onContinue={props.onContinueAfterVerification}
        />
      );
  }
}

/**
 * Главная: то же меню режимов, что на результате, плюс продолжение и выбор
 * пути обучения. Данных у экрана нет — всё берётся из состояния приложения.
 */
function HomeCase({ props }: { readonly props: ScreenRouterProps }): React.JSX.Element {
  return (
    <HomeScreen
      t={props.t}
      onContinue={props.onAgain}
      onOpenReport={props.onOpenReport}
      onOpenAchievements={props.onOpenAchievements}
      onOpenLeaderboard={props.onOpenLeaderboard}
      gameElements={props.gameElements}
      onSaveProgress={props.onSaveProgress}
      onStartControlTest={props.onStartControlTest}
      onStartMistakes={props.onStartMistakes}
      onStartSentences={props.onStartSentences}
      onStartFundamentals={props.onStartFundamentals}
      onStartSymbols={props.onStartSymbols}
      onStartPacks={props.onStartPacks}
      onStartLayoutSwitch={props.onStartLayoutSwitch}
      onStartTempo={props.onStartTempo}
      onStartCustomText={props.onStartCustomText}
      symbolModes={props.symbolModes}
      packModes={props.packModes}
      fundamentalsAvailable={props.fundamentalsAvailable}
      mistakesAvailable={props.mistakesAvailable}
      latin={props.latin}
      layoutSwitch={props.layoutSwitch}
      onTrainingLanguageChange={props.onTrainingLanguageChange}
      startPath={props.startPath}
      onStartPathChange={props.onStartPathChange}
      keys={props.keys}
    />
  );
}

/**
 * Объяснение закрытого режима.
 *
 * Чего не хватает, знает условие, а не экран: пакетам нужна латиница,
 * переключению — обе раскладки его упражнения.
 */
function LockedCase({
  target,
  props,
}: {
  readonly target: LockedTarget;
  readonly props: ScreenRouterProps;
}): React.JSX.Element {
  const readiness = target.kind === 'packs' ? props.latin : props.layoutSwitch;

  return (
    <LockedModeScreen
      t={props.t}
      target={target}
      readiness={readiness}
      onTrainScript={() => props.onTrainingLanguageChange(readiness.language)}
      onProceed={() => props.onProceedLocked(target)}
      onBack={props.onBack}
    />
  );
}

/** Экраны первого входа: в приложение и в новый язык тренировки. */
const ENTRY_SCREEN_NAMES = ['onboarding', 'language-setup'] as const;

type EntryScreen = Extract<AppScreen, { name: (typeof ENTRY_SCREEN_NAMES)[number] }>;

function isEntryScreen(screen: AppScreen): screen is EntryScreen {
  return (ENTRY_SCREEN_NAMES as readonly string[]).includes(screen.name);
}

/**
 * Первый вход: в приложение или в новый язык тренировки.
 *
 * Экраны разные, но повод один — приложение ещё не знает, чем и как человек
 * собирается заниматься, и спрашивает об этом.
 */
function EntryCase({
  t,
  screen,
  props,
}: {
  readonly t: Translate;
  readonly screen: EntryScreen;
  readonly props: ScreenRouterProps;
}): React.JSX.Element {
  if (screen.name === 'onboarding') {
    return <OnboardingScreen t={t} onStart={props.onOnboardingDone} />;
  }

  return (
    <LanguageSetupScreen
      t={t}
      language={screen.language}
      onStart={props.onLanguageSetupDone}
      onCancel={props.onLanguageSetupCancel}
    />
  );
}

/** Экраны отдельных режимов: у каждого свои данные, но один и тот же вход. */
const MODE_SCREEN_NAMES = [
  'my-mistakes',
  'sentences',
  'fundamentals',
  'symbols',
  'packs',
  'layout-switch',
  'tempo',
  'custom-text',
] as const;

type ModeScreen = Extract<AppScreen, { name: (typeof MODE_SCREEN_NAMES)[number] }>;

function isModeScreen(screen: AppScreen): screen is ModeScreen {
  return (MODE_SCREEN_NAMES as readonly string[]).includes(screen.name);
}

/**
 * Экран режима, открытого с результата.
 *
 * Режимы собраны в одну ветку: каждый из них — это «показать занятие и
 * сообщить о его конце», и разбирать их поимённо в корневом переключателе
 * значит растить его без нужды (ТЗ NFR-Q-02).
 */
function ModeCase({
  runtime,
  t,
  screen,
  props,
}: {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly screen: ModeScreen;
  readonly props: ScreenRouterProps;
}): React.JSX.Element {
  switch (screen.name) {
    case 'my-mistakes':
      return (
        <MistakesScreen
          runtime={runtime}
          t={t}
          session={screen.session}
          sessionId={screen.sessionId}
          onStageDone={props.onMistakeStageDone}
        />
      );

    case 'sentences':
      return (
        <SentencesScreen
          runtime={runtime}
          t={t}
          session={screen.session}
          sessionId={screen.sessionId}
          onSentenceDone={props.onSentenceDone}
        />
      );

    case 'fundamentals':
      return (
        <FundamentalsScreen
          runtime={runtime}
          t={t}
          plan={screen.plan}
          sessionId={screen.sessionId}
          onFinished={props.onFundamentalsDone}
        />
      );

    case 'symbols':
      return (
        <SymbolsScreen
          runtime={runtime}
          t={t}
          plan={screen.plan}
          sessionId={screen.sessionId}
          onFinished={props.onSymbolsDone}
        />
      );

    case 'packs':
      return (
        <PacksScreen
          runtime={runtime}
          t={t}
          plan={screen.plan}
          sessionId={screen.sessionId}
          onFinished={props.onPacksDone}
        />
      );

    case 'layout-switch':
      return (
        <LayoutSwitchScreen
          runtime={runtime}
          t={t}
          plan={screen.plan}
          sessionId={screen.sessionId}
          onFinished={props.onLayoutSwitchDone}
        />
      );

    case 'tempo':
      return (
        <TempoScreen
          runtime={runtime}
          t={t}
          session={screen.session}
          sessionId={screen.sessionId}
          onRunDone={props.onTempoRunDone}
        />
      );

    case 'custom-text':
      return (
        <CustomTextScreen
          runtime={runtime}
          t={t}
          plan={screen.plan}
          tooShort={screen.tooShort}
          sessionId={screen.sessionId}
          onTextSubmit={props.onCustomTextSubmit}
          onFinished={props.onCustomTextDone}
        />
      );
  }
}

/**
 * Экран результата.
 *
 * Выделен в отдельный случай, потому что режимов, доступных с результата,
 * стало больше, чем строк в остальных ветках вместе взятых.
 */
/**
 * Экраны итогов: измерения и занятия.
 *
 * Разбираются вместе, потому что отвечают на один вопрос — что получилось и
 * что делать дальше, — и различаются только тем, что именно закончилось.
 */
function ResultsCase({
  t,
  screen,
  props,
}: {
  readonly t: Translate;
  readonly screen: Extract<AppScreen, { name: 'result' | 'diagnostic-result' }>;
  readonly props: ScreenRouterProps;
}): React.JSX.Element {
  if (screen.name === 'diagnostic-result') {
    return (
      <DiagnosticResultScreen
        t={t}
        diagnostic={screen.diagnostic}
        onContinue={props.onContinueAfterDiagnostics}
        onStartFundamentals={props.onStartFundamentals}
        onRetake={props.onRetakeDiagnostics}
      />
    );
  }

  return <ResultCase screen={screen} props={props} />;
}

function ResultCase({
  screen,
  props,
}: {
  readonly screen: Extract<AppScreen, { name: 'result' }>;
  readonly props: ScreenRouterProps;
}): React.JSX.Element {
  return (
    <ResultScreen
      t={props.t}
      summary={screen.summary}
      practised={screen.practised}
      earned={screen.earned}
      resumesToFundamentals={props.resumesToFundamentals}
      gameElements={props.gameElements}
      onAgain={props.onAgain}
      onSaveProgress={props.onSaveProgress}
      onStartControlTest={props.onStartControlTest}
      onStartMistakes={props.onStartMistakes}
      onStartSentences={props.onStartSentences}
      onStartFundamentals={props.onStartFundamentals}
      onStartSymbols={props.onStartSymbols}
      symbolModes={props.symbolModes}
      onStartPacks={props.onStartPacks}
      packModes={props.packModes}
      onOpenReport={props.onOpenReport}
      onStartLayoutSwitch={props.onStartLayoutSwitch}
      onStartTempo={props.onStartTempo}
      onStartCustomText={props.onStartCustomText}
      fundamentalsAvailable={props.fundamentalsAvailable}
      mistakesAvailable={props.mistakesAvailable}
      retentionDue={props.retentionDue}
      latin={props.latin}
      layoutSwitch={props.layoutSwitch}
      onTrainingLanguageChange={props.onTrainingLanguageChange}
    />
  );
}

/** Экран персональной тренировки: стадия Memory Loop задаёт уровень подсказок. */
function PracticeScreen({
  runtime,
  t,
  screen,
  onFinished,
}: {
  readonly runtime: Runtime;
  readonly t: Translate;
  readonly screen: Extract<AppScreen, { name: 'practice' }>;
  readonly onFinished: (session: TypingSession, signals: IntegritySignals) => void;
}): React.JSX.Element {
  const stage = screen.plan.memoryLoop[0]?.stage ?? 'type';

  return (
    <TypingScreen
      key={screen.sessionId}
      runtime={runtime}
      t={t}
      title={t('practice.title')}
      hint={t(`practice.stage.${stage}`)}
      sessionId={screen.sessionId}
      layoutId={screen.plan.exercise.layoutId}
      targetText={screen.plan.exercise.text}
      hintLevel={hintLevelFor(stage)}
      onFinished={onFinished}
    />
  );
}
